import { extractErrorMessage } from '@/lib'
import { useAuthResetPassword } from '@jobilla/nexus-sdk'
import { createFileRoute, Link } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	AuthErrorAlert,
	AuthInput,
	AuthLayout,
	AuthSubmitButton,
	ForgotPasswordForm,
	JobillaBrandLogo,
	logger,
} from '../ui'

interface ResetPasswordSearch {
	token?: string
}

export const Route = createFileRoute('/reset-password')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => ({
		token: (search.token as string) || undefined,
	}),
})

function RouteComponent() {
	const { token } = Route.useSearch()

	if (token) {
		return (
			<AuthLayout>
				<ResetPasswordForm token={token} />
			</AuthLayout>
		)
	}

	return (
		<AuthLayout>
			<ForgotPasswordForm />
		</AuthLayout>
	)
}

function ResetPasswordForm({ token }: { token: string }) {
	const { t } = useTranslation('common')
	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isSuccess, setIsSuccess] = useState(false)
	const resetPassword = useAuthResetPassword()

	function handleSubmit(event: FormEvent) {
		event.preventDefault()
		setError(null)

		if (newPassword !== confirmPassword) {
			setError(t('auth.resetPassword.passwordsMismatch', 'Passwords do not match'))
			return
		}

		resetPassword.mutate({ data: { token, newPassword } }, {
			onSuccess: () => setIsSuccess(true),
			onError: (err) => {
				const status = isAxiosError(err) ? err.response?.status : undefined
				if (!status || status >= 500) {
					logger.error(err, { category: 'auth', data: { action: 'reset-password' } })
				} else {
					logger.warn('Password reset failed', {
						category: 'auth',
						data: { status, action: 'reset-password' },
					})
				}
				setError(extractErrorMessage(err, t('auth.resetPassword.errorDefault', 'Failed to reset password')))
			},
		})
	}

	if (isSuccess) {
		return (
			<div className="w-full max-w-2xl mx-auto p-8">
				<JobillaBrandLogo />

				<div className="mt-8">
					<h1 className="text-3xl font-semibold text-gray-800 tracking-[0.01em]">
						{t('auth.resetPassword.successTitle', 'Password reset')}
					</h1>
					<p className="mt-3 text-gray-600">
						{t(
							'auth.resetPassword.successMessage',
							'Your password has been reset successfully. You can now sign in with your new password.',
						)}
					</p>

					<div className="mt-8">
						<Link
							to="/auth"
							className="block w-full text-center py-3 px-4 rounded-full font-medium text-white text-base bg-gradient-to-r from-[#225DFC] to-[#9726FA] hover:brightness-130 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/50 focus:ring-offset-2 shadow-lg shadow-[#225DFC]/30 hover:shadow-xl hover:translate-y-[-1px] transition-all duration-400"
						>
							{t('auth.resetPassword.signInLink', 'Sign in with your new password')}
						</Link>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="w-full max-w-2xl mx-auto p-8">
			<JobillaBrandLogo />

			<div className="mt-8">
				<h1 className="text-3xl font-semibold text-gray-800 tracking-[0.01em]">
					{t('auth.resetPassword.title', 'Reset your password')}
				</h1>
				<p className="mt-2 text-gray-600">
					{t('auth.resetPassword.description', 'Enter your new password below.')}
				</p>
			</div>

			<form onSubmit={handleSubmit} className="mt-8 space-y-8">
				<AuthErrorAlert message={error} />

				<AuthInput
					label={t('auth.resetPassword.newPasswordLabel', 'New password')}
					type="password"
					placeholder=""
					value={newPassword}
					onChange={setNewPassword}
					required={true}
					minLength={8}
					autoComplete="new-password"
				/>

				<AuthInput
					label={t('auth.resetPassword.confirmPasswordLabel', 'Confirm new password')}
					type="password"
					placeholder=""
					value={confirmPassword}
					onChange={setConfirmPassword}
					required={true}
					minLength={8}
					autoComplete="new-password"
				/>

				<AuthSubmitButton isLoading={resetPassword.isPending}>
					{t('auth.resetPassword.submit', 'Reset password')}
				</AuthSubmitButton>

				<p className="text-center text-sm text-gray-600">
					<Link to="/auth" className="text-[#6366F1] hover:text-[#4F46E5] font-medium">
						{t('auth.resetPassword.backToSignIn', 'Back to sign in')}
					</Link>
				</p>
			</form>
		</div>
	)
}
